import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useCallback, useState, useRef } from 'react';
import { ProductImageSizesPortraitEnum } from '@as-react/common-types';
import { Carousel, CarouselVariant, ProductPreviewMainSlide, SliderNavVariant, } from '@as/cotopaxi';
import { Video } from '../../../video/Video';
import { getVideoPlayerType } from '../../../../util/VideoUtil';
import LabelsOverlay from '../../../common/labels-overlay/LabelsOverlay';
import { useSlidesDisplayState } from '../../../../hooks/useSlidesDisplayState';
import { useProductMedia } from '../../product-media/useProductMedia';
import { SlideInViewFrom } from '../ProductPreview';
import { HtmlInclude } from '../../../common/html-include/HtmlInclude';
import { useIsClient } from '@yonderland/andes-react';
const BACKGROUND_LOAD_IMAGES_THRESHOLD = 2;
const ProductPreviewSlide = (_a) => {
    var { shouldLoadBackgroundImage, smallImagePath } = _a, rest = __rest(_a, ["shouldLoadBackgroundImage", "smallImagePath"]);
    const [didLoadImage, setDidLoadImage] = useState(!shouldLoadBackgroundImage);
    useEffect(() => {
        if (!shouldLoadBackgroundImage) {
            return;
        }
        setDidLoadImage(false);
        const imageLoader = new Image();
        imageLoader.src = smallImagePath;
        imageLoader.decoding = 'sync';
        imageLoader.onload = () => {
            setDidLoadImage(true);
        };
    }, [smallImagePath, shouldLoadBackgroundImage]);
    return _jsx("div", { children: didLoadImage ? _jsx(ProductPreviewMainSlide, Object.assign({ smallImagePath: smallImagePath }, rest)) : null });
};
const getStyleFragment = () => `
  .swiper-slide.slidesPerView-1-1 {
    width: calc(90% + 4px);
  }
  .swiper-slide.slidesPerView-1-2 {
    width: calc(82.5% + 4px);
  }
  .swiper-slide.slidesPerView-1-1,
  .swiper-slide.slidesPerView-1-2 {
    margin-right: 4px;
  }
`;
export const ProductPreviewMainSlider = ({ activeSlideIndex, selectedColorId, onActiveSlideIndexChange, onUpdateSlideInView, showLabels, popoverHandlers, slidesPerView, }) => {
    var _a;
    const { mediaCollection, alt, labelsConfig } = useProductMedia();
    const gallerySwiper = useRef(null);
    const { handlePopoverOpen } = popoverHandlers;
    const slidesDisplayState = useSlidesDisplayState(activeSlideIndex, mediaCollection.length, (_a = mediaCollection === null || mediaCollection === void 0 ? void 0 : mediaCollection[activeSlideIndex + 1]) === null || _a === void 0 ? void 0 : _a.isVideo);
    const firstSlideRef = useRef(null);
    const isObserverTriggered = useRef(false);
    const isClient = useIsClient();
    const onGallerySlideChange = function () {
        const { snapIndex } = this;
        if (snapIndex !== activeSlideIndex) {
            onActiveSlideIndexChange(snapIndex);
            onUpdateSlideInView === null || onUpdateSlideInView === void 0 ? void 0 : onUpdateSlideInView(snapIndex, SlideInViewFrom.CAROUSEL);
        }
    };
    useEffect(() => {
        if (!onUpdateSlideInView || !firstSlideRef.current || isObserverTriggered.current) {
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry === null || entry === void 0 ? void 0 : entry.isIntersecting) {
                onUpdateSlideInView(0, SlideInViewFrom.CAROUSEL);
                observer.disconnect();
                isObserverTriggered.current = true;
            }
        }, {
            threshold: 0.1,
        });
        observer.observe(firstSlideRef.current);
        return () => {
            observer.disconnect();
        };
    }, [selectedColorId, onUpdateSlideInView, firstSlideRef.current]);
    useEffect(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.on('slideChange', onGallerySlideChange);
        return () => {
            var _a;
            (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.off('slideChange');
        };
    }, [mediaCollection]);
    const renderSlide = useCallback((image, index, altCopy) => (_jsx("div", { ref: index === 0 ? firstSlideRef : undefined, children: slidesDisplayState[index] && (_jsx(ProductPreviewSlide, { shouldLoadBackgroundImage: index > BACKGROUND_LOAD_IMAGES_THRESHOLD, smallImagePath: image.sourceSet[ProductImageSizesPortraitEnum.detail], largeImagePath: image.sourceSet[ProductImageSizesPortraitEnum.zoom], videoComponent: image.video ? (_jsx(Video, { url: image.video.productVideoDetail.url, playerType: getVideoPlayerType(image.video.productVideoDetail.url), stop: index !== activeSlideIndex })) : undefined, alt: altCopy, onClick: !image.isVideo ? handlePopoverOpen : undefined })) }, `image_${index}`)), [activeSlideIndex, slidesDisplayState]);
    const productMainSliderCarouselConfig = {
        variant: CarouselVariant.MAIN,
        arrowsConfig: {
            variant: SliderNavVariant.SEMICIRCLE,
        },
        showPagination: true,
        configurationOverrides: {
            slideClass: isClient
                ? 'swiper-slide'
                : `swiper-slide slidesPerView-${slidesPerView.toString().replace('.', '-')}`,
            normalizeSlideIndex: false,
            shouldSwiperUpdate: true,
            getSwiper: (instance) => {
                gallerySwiper.current = instance;
            },
            spaceBetween: 4,
            centeredSlides: true,
            watchOverflow: true,
            slidesPerView,
            centeredSlidesBounds: slidesPerView > 1,
        },
    };
    const renderMainCarouselOverlay = showLabels ? (_jsx(LabelsOverlay, Object.assign({}, labelsConfig, { hasWideGutter: true, dataQA: "product_image_overlay" }))) : undefined;
    useEffect(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.slideTo(activeSlideIndex);
    }, [activeSlideIndex]);
    useEffect(() => {
        var _a;
        (_a = gallerySwiper.current) === null || _a === void 0 ? void 0 : _a.update();
    }, [mediaCollection]);
    return isClient ? (_jsx(Carousel, Object.assign({}, productMainSliderCarouselConfig, { dataQA: "product_main_slider", overlay: renderMainCarouselOverlay, children: mediaCollection.map((image, index) => renderSlide(image, index, alt)) }))) : (_jsxs(_Fragment, { children: [_jsx(HtmlInclude, { styleFragment: getStyleFragment() }), _jsx(Carousel, Object.assign({}, productMainSliderCarouselConfig, { dataQA: "product_main_slider", overlay: renderMainCarouselOverlay, children: mediaCollection.map((image, index) => renderSlide(image, index, alt)) }))] }));
};
